import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { CreateReferralUrlRequest, ReferralUrlService } from './../../services/referral-url.service';
import { SalesService } from '../../services/sales-agent.service';
import { AuthService } from '../../../../core/services/auth.service';

import { switchMap } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '../../../../../environments/environment';
import { SalesAgent } from '../../models/sales-agent.model';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ReferralHelpDialogComponent } from '../referral-help/referral-help';
import { of } from 'rxjs';

@Component({
  selector: 'app-referral-url',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule, // Added for [(ngModel)] binding
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSnackBarModule,
    MatCardModule,
    MatDialogModule,
    MatIconModule
  ],
  templateUrl: './referral-url.html',
  styleUrls: ['./referral-url.scss']
})
export class ReferralUrlComponent implements OnInit {
  cdr = inject(ChangeDetectorRef);
  auth = inject(AuthService);
  salesService = inject(SalesService);
  referralUrlService = inject(ReferralUrlService);
  dialog = inject(MatDialog);
  snackBar = inject(MatSnackBar); // Injected to handle copy and save notifications
  salesAgent: SalesAgent | null = null;
  referralUrl: string | null = null;
  isLoading = true;
  isEditing = false; // Tracks the state of the input field
  referralUrlClicks = 0;

  ngOnInit(): void {
    const currentUser = this.auth.currentUser();

    if (!currentUser?.id) {
      console.warn('No authenticated user found');
      this.isLoading = false;
      return;
    }

    this.salesService.getSalesAgentByUserId(currentUser.id)
      .pipe(
        switchMap(agent => {
          if (!agent?.agentId) {
            throw new Error('No sales agent found for this user');
          }
          this.salesAgent = agent;
          return this.referralUrlService.getByAgentCode(agent.agentId);
        })
      )
      .subscribe({
        next: (referrals) => {
            console.log('referrals', referrals);
          this.isLoading = false;
          this.referralUrl = referrals?.length > 0 ? referrals[0].referralUrl : null;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;
          console.error('Failed to load referral URL', err);
        }
      });
  }

  toggleEdit(): void {
    this.isEditing = !this.isEditing;
    
    if (!this.isEditing && this.salesAgent !== null && this.salesAgent.agentId !== undefined && this.referralUrl) {
      const request: CreateReferralUrlRequest = {
        agentCode: this.salesAgent.agentId,
        referralUrl: this.referralUrl
      };

      this.referralUrlService.createReferralUrl(request).subscribe({
        next: (response) => {
            this.snackBar.open('Referral URL saved', ' ', { 
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['success-snackbar']
            });
        },
        error: (err) => {
          console.error('Failed to save referral URL', err);
          this.snackBar.open('Failed to save referral URL', ' ', { 
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
            });
        }
      })
      
    }
  }

  // Copies the full URL to the user's clipboard
  copyToClipboard(): void {
    if (!this.referralUrl) return;

    const fullUrl = `${environment.appUrl}/refer/${this.referralUrl}`;
    
    navigator.clipboard.writeText(fullUrl).then(() => {
      this.snackBar.open('Referral link copied', ' ', { 
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
        });
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      this.snackBar.open('Failed to copy link', ' ', { 
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
            });
    });
  }

  sanitizeUrlInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    
    // Regex: Match anything that is NOT a letter, number, or hyphen
    const invalidCharsRegex = /[^a-zA-Z0-9-]/g;
    
    // Clean the value and force it to lowercase for clean URLs
    const sanitizedValue = inputElement.value
      .replace(invalidCharsRegex, '')
      .toLowerCase();

    // If the value changed during sanitization, update the model and input field
    if (inputElement.value !== sanitizedValue) {
      inputElement.value = sanitizedValue;
      this.referralUrl = sanitizedValue;
    }
  }

  referralHelp() {
    this.dialog.open(ReferralHelpDialogComponent, {
      maxHeight: '70vh',           // ← Limits dialog height
      width: '620px',
      panelClass: ['glass-dialog-panel','referral-help-dialog-panel'],
      backdropClass: 'glass-dialog-backdrop'
    });
  }
}